import { createContext, useContext, useRef, useState } from "react";

/**
 * SopTasksContext
 *
 * Holds the list of background PDF-extraction tasks at app-root level so that:
 *  - The floating BackgroundTaskPanel survives route navigation
 *  - Pollers are never cancelled when the SOPs screen unmounts
 *
 * Shape of each task:
 *   { id, fileName, partId, status: 'processing'|'completed'|'failed', progress, make, model }
 */

const SopTasksContext = createContext(null);

export const SopTasksProvider = ({ children }) => {
  const [tasks, setTasks] = useState([]);
  const [tasksPanelExpanded, setTasksPanelExpanded] = useState(true);

  /**
   * taskControllersRef: { [taskId]: { cancelled: boolean } }
   * This lives in the context (not in the SOPs component) so pollers
   * are NOT cancelled when SOPs unmounts on navigation.
   */
  const taskControllersRef = useRef({});

  /**
   * onViewResultsRef: set by SOPs when it mounts so the global panel can
   * trigger the results modal even when it lives outside the route.
   * Cleared by SOPs on unmount.
   */
  const onViewResultsRef = useRef(null);

  /**
   * onTryAgainRef: set by SOPs when it mounts so the global panel can
   * open the upload/extract modal when the user clicks "Try Again".
   * Cleared by SOPs on unmount.
   */
  const onTryAgainRef = useRef(null);

  /**
   * pendingViewTaskId: when the user clicks "View Results" from the global
   * panel while on a different route, we store the taskId here.
   * SOPs reads this on mount and auto-opens the results modal.
   */
  const [pendingViewTaskId, setPendingViewTaskId] = useState(null);

  /**
   * viewingTaskId: the taskId whose "View Results" is currently loading.
   * Set by the panel before calling the handler; cleared by the SOPs
   * component once the results modal has opened (or on error).
   */
  const [viewingTaskId, setViewingTaskId] = useState(null);

  return (
    <SopTasksContext.Provider
      value={{
        tasks,
        setTasks,
        tasksPanelExpanded,
        setTasksPanelExpanded,
        taskControllersRef,
        onViewResultsRef,
        onTryAgainRef,
        pendingViewTaskId,
        setPendingViewTaskId,
        viewingTaskId,
        setViewingTaskId,
      }}
    >
      {children}
    </SopTasksContext.Provider>
  );
};

export const useSopTasks = () => {
  const ctx = useContext(SopTasksContext);
  if (!ctx) {
    throw new Error("useSopTasks must be used within a SopTasksProvider");
  }
  return ctx;
};

export default SopTasksContext;
